import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Sidebar from '@/components/Sidebar';
import api from '@/lib/api';
import './Dashboard.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR');
};

const Dashboard = () => {
  const { user, activeOperator } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [showMonthlyRevenue, setShowMonthlyRevenue] = useState(false);

  useEffect(() => {
    api.get('/dashboard/stats').then(res => setStats(res.data)).catch(() => setStats({}));
  }, []);

  const s = stats || {};

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="dashboard-header">
          <h1 data-testid="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle" data-testid="dashboard-subtitle">
            Bienvenido, {activeOperator?.name || user?.name}
          </p>
        </div>

        <div className="metrics-grid">
          <div className="metric-card" data-testid="metric-budgets-month">
            <div className="metric-value mono">{s.approved_budgets ?? 0}</div>
            <div className="metric-label">Presupuestos aprobados</div>
          </div>

          <div className="metric-card" data-testid="metric-clients-total">
            <div className="metric-value mono">{s.total_clients ?? 0}</div>
            <div className="metric-label">Clientes totales</div>
          </div>

          <div className="metric-card" data-testid="metric-products-total">
            <div className="metric-value mono">{s.total_products ?? 0}</div>
            <div className="metric-label">Productos totales</div>
          </div>

          <div className="metric-card" data-testid="metric-revenue-month">
            <div className="metric-card-top">
              <div>
                <div className="metric-value mono">
                  {showMonthlyRevenue ? formatMoney(s.revenue_this_month) : 'Oculto'}
                </div>
                <div className="metric-label">Aprobados del mes</div>
              </div>
              <button
                type="button"
                className="metric-toggle-btn"
                onClick={() => setShowMonthlyRevenue(prev => !prev)}
                data-testid="toggle-monthly-revenue"
              >
                {showMonthlyRevenue ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>
        </div>

        <div className="content-grid">
          <div className="card">
            <h3 className="card-title" data-testid="recent-budgets-title">Presupuestos Recientes</h3>
            {(s.recent_budgets || []).length === 0 ? (
              <div className="empty-state" data-testid="empty-budgets">
                <p>No hay presupuestos registrados aún</p>
              </div>
            ) : (
              <ul className="recent-list">
                {s.recent_budgets.map(b => (
                  <li key={b.id}>
                    <Link to={`/presupuestos/${b.id}`} className="recent-item" data-testid={`recent-budget-${b.id}`}>
                      <div>
                        <div className="recent-title mono">#{String(b.budget_number).padStart(4, '0')} — {b.client_snapshot?.name}</div>
                        <div className="recent-sub">{b.product_snapshot?.name} · {formatDate(b.created_at)}</div>
                      </div>
                      <div className="mono cell-strong">{formatMoney(b.final_price)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card">
            <h3 className="card-title" data-testid="recent-clients-title">Clientes Recientes</h3>
            {(s.recent_clients || []).length === 0 ? (
              <div className="empty-state" data-testid="empty-clients">
                <p>No hay clientes registrados aún</p>
              </div>
            ) : (
              <ul className="recent-list">
                {s.recent_clients.map(c => (
                  <li key={c.id}>
                    <Link to="/clientes" className="recent-item" data-testid={`recent-client-${c.id}`}>
                      <div>
                        <div className="recent-title">{c.name}</div>
                        <div className="recent-sub">{c.phone || c.email || 'Sin contacto'}</div>
                      </div>
                      <div className="recent-sub">{formatDate(c.created_at)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="quick-actions">
          <h3 className="section-title">Accesos Rápidos</h3>
          <div className="actions-grid">
            <button
              className="action-btn"
              data-testid="quick-action-new-budget"
              onClick={() => navigate('/presupuestos/nuevo')}
            >
              <span className="action-icon">+</span>
              <span>Nuevo Presupuesto</span>
            </button>
            <button
              className="action-btn"
              data-testid="quick-action-new-client"
              onClick={() => navigate('/clientes')}
            >
              <span className="action-icon">+</span>
              <span>Nuevo Cliente</span>
            </button>
            <button
              className="action-btn"
              data-testid="quick-action-new-product"
              onClick={() => navigate('/productos/nuevo')}
            >
              <span className="action-icon">+</span>
              <span>Nuevo Producto</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
